import React, { Fragment, useEffect, useState, useMemo } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Badge,
  Input,
} from "reactstrap";
import { ChevronRight, Package, FolderPlus, X, Search } from "react-feather";
import Breadcrumb from "../../common/breadcrumb";
import { useSelector } from "react-redux";
import Loader from "../../../shared/Loader";
import { errorToast } from "../../../shared/Toast";
import { Currency } from "../../../constants/currency";
import {
  getSystemProductListing,
  getSystemProductCategoryListing,
  assignSystemProductsToCategory,
  removeSystemProductFromCategory,
} from "../../../utils/apiCalls";

const AssignSystemProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const userType = localStorage.getItem("userType");
  const storedMerchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const selectedMerchant = userType === "merchant" ? storedMerchantId : reduxSelectedMerchant;

  const fetchData = async () => {
    if (!selectedMerchant || selectedMerchant === "all") return;
    setLoading(true);
    try {
      const [productsData, categoriesData] = await Promise.all([
        getSystemProductListing(selectedMerchant),
        getSystemProductCategoryListing(selectedMerchant),
      ]);
      setProducts(productsData || []);
      setCategories(categoriesData || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedMerchant]);

  const categoryProducts = useMemo(() => {
    if (!selectedCategory) return [];
    return products.filter((p) => p.SystemProductCategoryId === selectedCategory.id);
  }, [products, selectedCategory]);

  const unassignedProducts = useMemo(() => {
    return products.filter((p) => !p.SystemProductCategoryId);
  }, [products]);

  const filteredUnassigned = useMemo(() => {
    if (!searchTerm) return unassignedProducts;
    const term = searchTerm.toLowerCase();
    return unassignedProducts.filter(
      (p) =>
        p.title?.toLowerCase().includes(term) ||
        p.merchant_product_id?.toLowerCase().includes(term)
    );
  }, [unassignedProducts, searchTerm]);

  const handleToggleProduct = (productId) => {
    setSelectedProducts((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
  };

  const handleSelectAll = () => {
    if (selectedProducts.length === filteredUnassigned.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(filteredUnassigned.map((p) => p.id));
    }
  };

  const handleAssign = async () => {
    if (!selectedCategory) {
      errorToast("Please select a category first.");
      return;
    }
    if (selectedProducts.length === 0) {
      errorToast("Please select at least one product.");
      return;
    }
    const result = await assignSystemProductsToCategory(selectedCategory.id, selectedProducts);
    if (result) {
      setSelectedProducts([]);
      fetchData();
    }
  };

  const handleRemoveFromCategory = async (productId) => {
    const result = await removeSystemProductFromCategory(productId);
    if (result) {
      fetchData();
    }
  };

  const noMerchant = !selectedMerchant || selectedMerchant === "all";

  return (
    <Fragment>
      <Breadcrumb title="Assign System Products" parent="Products" />
      <Container fluid={true}>
        {loading ? (
          <Loader />
        ) : noMerchant ? (
          <Card>
            <CardBody>
              <div className="text-center text-muted py-5">
                <Package size={48} className="mb-3" style={{ opacity: 0.3 }} />
                <p>Please select a merchant to manage system product assignments.</p>
              </div>
            </CardBody>
          </Card>
        ) : (
          <Row>
            {/* Categories sidebar */}
            <Col md="3">
              <Card style={{ position: "sticky", top: "80px" }}>
                <CardHeader style={{ background: "#f8f9fa", borderBottom: "2px solid #009689" }}>
                  <h6 className="mb-0" style={{ fontWeight: 600 }}>Categories</h6>
                </CardHeader>
                <CardBody style={{ padding: 0, maxHeight: "calc(100vh - 240px)", overflowY: "auto" }}>
                  {categories.length === 0 ? (
                    <div className="text-center py-4 px-3">
                      <FolderPlus size={32} className="mb-2" style={{ opacity: 0.3 }} />
                      <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
                        No categories yet. Create one from the System Product Categories page.
                      </p>
                    </div>
                  ) : (
                    categories.map((cat) => {
                      const isActive = selectedCategory?.id === cat.id;
                      const count = products.filter((p) => p.SystemProductCategoryId === cat.id).length;
                      return (
                        <div
                          key={cat.id}
                          onClick={() => {
                            setSelectedCategory(cat);
                            setSelectedProducts([]);
                          }}
                          style={{
                            padding: "12px 16px",
                            cursor: "pointer",
                            borderBottom: "1px solid #f0f0f0",
                            backgroundColor: isActive ? "#f0fdfa" : "#fff",
                            borderLeft: isActive ? "3px solid #009689" : "3px solid transparent",
                            transition: "all 0.15s ease",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            {isActive && <ChevronRight size={14} style={{ color: "#009689" }} />}
                            <span style={{
                              fontWeight: isActive ? 600 : 400,
                              color: isActive ? "#009689" : "#333",
                              fontSize: "14px",
                            }}>
                              {cat.name}
                            </span>
                          </div>
                          <Badge
                            pill
                            style={{
                              backgroundColor: isActive ? "#009689" : "#e9ecef",
                              color: isActive ? "#fff" : "#666",
                              fontSize: "11px",
                              minWidth: "24px",
                            }}
                          >
                            {count}
                          </Badge>
                        </div>
                      );
                    })
                  )}
                </CardBody>
              </Card>
            </Col>

            {/* Main content */}
            <Col md="9">
              {/* Category products */}
              {selectedCategory && (
                <Card className="mb-3">
                  <CardHeader
                    className="d-flex justify-content-between align-items-center"
                    style={{ background: "#f0fdfa" }}
                  >
                    <div>
                      <h6 className="mb-0" style={{ fontWeight: 600 }}>
                        {selectedCategory.name}
                        <Badge
                          pill
                          className="ms-2"
                          style={{ backgroundColor: "#009689", fontSize: "11px" }}
                        >
                          {categoryProducts.length} product{categoryProducts.length !== 1 ? "s" : ""}
                        </Badge>
                      </h6>
                    </div>
                  </CardHeader>
                  <CardBody style={{ padding: 0 }}>
                    {categoryProducts.length === 0 ? (
                      <div className="text-center py-4">
                        <Package size={32} className="mb-2" style={{ opacity: 0.3 }} />
                        <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
                          No products assigned yet. Select products below to assign.
                        </p>
                      </div>
                    ) : (
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr style={{ borderBottom: "2px solid #dee2e6" }}>
                            <th style={tableHeaderStyle}>Product</th>
                            <th style={tableHeaderStyle}>Price</th>
                            <th style={tableHeaderStyle}>External ID</th>
                            <th style={{ ...tableHeaderStyle, width: "90px", textAlign: "center" }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {categoryProducts.map((p) => (
                            <tr key={p.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                              <td style={tableCellStyle}>
                                <span style={{ fontWeight: 500 }}>{p.title}</span>
                              </td>
                              <td style={tableCellStyle}>
                                {p.price} {Currency.sr}
                              </td>
                              <td style={tableCellStyle}>
                                <span style={{ color: "#999", fontSize: "13px" }}>
                                  {p.merchant_product_id}
                                </span>
                              </td>
                              <td style={{ ...tableCellStyle, textAlign: "center" }}>
                                <button
                                  onClick={() => handleRemoveFromCategory(p.id)}
                                  title="Remove from category"
                                  style={{
                                    background: "none",
                                    border: "1px solid #ffcdd2",
                                    borderRadius: "50%",
                                    width: "28px",
                                    height: "28px",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    color: "#009689",
                                    transition: "all 0.15s ease",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = "#009689";
                                    e.currentTarget.style.color = "#fff";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = "transparent";
                                    e.currentTarget.style.color = "#009689";
                                  }}
                                >
                                  <X size={14} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </CardBody>
                </Card>
              )}

              {/* Unassigned products */}
              <Card>
                <CardHeader
                  className="d-flex justify-content-between align-items-center"
                  style={{ background: "#f8f9fa" }}
                >
                  <h6 className="mb-0" style={{ fontWeight: 600 }}>
                    Unassigned Products
                    <Badge
                      pill
                      className="ms-2"
                      style={{ backgroundColor: "#6c757d", fontSize: "11px" }}
                    >
                      {unassignedProducts.length}
                    </Badge>
                  </h6>
                  <div className="d-flex gap-2 align-items-center">
                    <div style={{ position: "relative" }}>
                      <Search
                        size={14}
                        style={{
                          position: "absolute",
                          left: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "#999",
                        }}
                      />
                      <Input
                        type="text"
                        placeholder="Search products..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ width: "220px", paddingLeft: "32px", fontSize: "13px" }}
                      />
                    </div>
                    {selectedCategory && selectedProducts.length > 0 && (
                      <Button
                        size="sm"
                        style={{
                          background: "linear-gradient(135deg, #009689 0%, #ff6b6b 100%)",
                          border: "none",
                          fontWeight: 500,
                          padding: "6px 16px",
                          whiteSpace: "nowrap",
                        }}
                        onClick={handleAssign}
                      >
                        Assign {selectedProducts.length} to "{selectedCategory.name}"
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardBody style={{ padding: 0 }}>
                  {!selectedCategory ? (
                    <div className="text-center py-5">
                      <ChevronRight size={32} className="mb-2" style={{ opacity: 0.2 }} />
                      <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
                        Select a category from the left to start assigning products.
                      </p>
                    </div>
                  ) : filteredUnassigned.length === 0 ? (
                    <div className="text-center py-5">
                      <Package size={32} className="mb-2" style={{ opacity: 0.2 }} />
                      <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
                        {searchTerm
                          ? "No unassigned products match your search."
                          : "All products have been assigned to categories."}
                      </p>
                    </div>
                  ) : (
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ borderBottom: "2px solid #dee2e6" }}>
                          <th style={{ ...tableHeaderStyle, width: "40px", textAlign: "center" }}>
                            <Input
                              type="checkbox"
                              checked={
                                filteredUnassigned.length > 0 &&
                                selectedProducts.length === filteredUnassigned.length
                              }
                              onChange={handleSelectAll}
                              style={{ cursor: "pointer" }}
                            />
                          </th>
                          <th style={tableHeaderStyle}>Product</th>
                          <th style={tableHeaderStyle}>Price</th>
                          <th style={tableHeaderStyle}>External ID</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUnassigned.map((p) => {
                          const isSelected = selectedProducts.includes(p.id);
                          return (
                            <tr
                              key={p.id}
                              onClick={() => handleToggleProduct(p.id)}
                              style={{
                                borderBottom: "1px solid #f0f0f0",
                                cursor: "pointer",
                                backgroundColor: isSelected ? "#f0fdfa" : "#fff",
                                transition: "background-color 0.1s ease",
                              }}
                              onMouseEnter={(e) => {
                                if (!isSelected) e.currentTarget.style.backgroundColor = "#fafafa";
                              }}
                              onMouseLeave={(e) => {
                                if (!isSelected) e.currentTarget.style.backgroundColor = "#fff";
                              }}
                            >
                              <td style={{ ...tableCellStyle, textAlign: "center", width: "40px" }}>
                                <Input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleToggleProduct(p.id)}
                                  style={{ cursor: "pointer" }}
                                />
                              </td>
                              <td style={tableCellStyle}>
                                <span style={{ fontWeight: 500 }}>{p.title}</span>
                              </td>
                              <td style={tableCellStyle}>
                                {p.price} {Currency.sr}
                              </td>
                              <td style={tableCellStyle}>
                                <span style={{ color: "#999", fontSize: "13px" }}>
                                  {p.merchant_product_id}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </Container>
    </Fragment>
  );
};

const tableHeaderStyle = {
  padding: "10px 12px",
  textAlign: "left",
  background: "#f2f2f2",
  borderBottom: "1px solid #dee2e6",
  fontSize: "13px",
  fontWeight: 600,
  color: "#555",
  textTransform: "uppercase",
  letterSpacing: "0.3px",
};

const tableCellStyle = {
  padding: "10px 12px",
  textAlign: "left",
  fontSize: "14px",
};

export default AssignSystemProducts;
